import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Button, LinearProgress } from '@mui/material';
import { useAppDispatch } from '../store';
import { addMessage } from '../store/localApp';
import { selectAuthUser } from '../store/selectors';
import {
  CompanySettings,
  CompanySettingsPatch,
  fetchCompanySettings,
  updateCompanySettings,
} from '../store/companySettings';
import { useAppTranslation } from '../services/i18n';
import { useCurrentSpace } from '../services/currentSpace';
import { setShellStateParam, syncShellCompanySpace } from '../services/ngShell';
import { PageTab, PageTabs } from '../components/PageTabs';
import { OrganizationSettings } from '../components/settings/OrganizationSettings';
import { RegistrationSettings } from '../components/settings/RegistrationSettings';
import { WorkplaceSettings } from '../components/settings/WorkplaceSettings';
import { ContentSettings } from '../components/settings/ContentSettings';
import { revertedValues, SettingsTabProps } from '../components/settings/settingsTab';

export type CompanySettingsTab = 'organization' | 'registration' | 'workplaces' | 'content';

const TABS: CompanySettingsTab[] = ['organization', 'registration', 'workplaces', 'content'];

const tabFromUrl = (): CompanySettingsTab | undefined => {
  const tab = new URLSearchParams(window.location.hash.split('?')[1]).get('tab');
  return TABS.includes(tab as CompanySettingsTab) ? (tab as CompanySettingsTab) : undefined;
};

export const CompanySettingsPage = () => {
  const dispatch = useAppDispatch();
  const t = useAppTranslation();
  const authUser = useSelector(selectAuthUser);
  const space = useCurrentSpace();
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [settings, setSettings] = useState<CompanySettings>();
  const [loadFailed, setLoadFailed] = useState(false);
  // the latest state for handlers that resolve after other saves have already landed
  const settingsRef = useRef<CompanySettings>();
  settingsRef.current = settings;
  const pendingSaves = useRef(0);
  const reloadWhenIdle = useRef(false);

  const loadSettings = useCallback(() => {
    setLoadFailed(false);
    return dispatch(fetchCompanySettings()).then((action) => {
      if (fetchCompanySettings.fulfilled.match(action)) {
        setSettings(action.payload);
      } else {
        setLoadFailed(true);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // A tab click rewrites ?tab with replaceState, which the hash router never hears about, so
  // the page tracks the tab itself and follows only real navigation (a link, the history).
  useEffect(() => {
    const followUrl = () => {
      const tab = tabFromUrl();
      if (tab) {
        setActiveTab(tab);
      }
    };
    window.addEventListener('hashchange', followUrl);
    window.addEventListener('popstate', followUrl);
    return () => {
      window.removeEventListener('hashchange', followUrl);
      window.removeEventListener('popstate', followUrl);
    };
  }, []);

  const onSave = useCallback(
    async (patch: CompanySettingsPatch, toastKey = 'companySettings.saved') => {
      const { file_id, ...values } = patch;
      const previous = settingsRef.current!;
      setSettings((current) => ({ ...current!, ...values }));

      pendingSaves.current++;
      const action = await dispatch(updateCompanySettings(patch));
      pendingSaves.current--;
      if (!updateCompanySettings.fulfilled.match(action)) {
        // the error toast comes from the API middleware; once no other save is on its way,
        // the server's state replaces whatever the optimistic updates left behind
        setSettings((current) => ({ ...current!, ...revertedValues(patch, previous, current!) }));
        reloadWhenIdle.current = true;
      }
      if (pendingSaves.current === 0 && reloadWhenIdle.current) {
        reloadWhenIdle.current = false;
        loadSettings();
      }
      if (!updateCompanySettings.fulfilled.match(action)) {
        return false;
      }

      const logo = file_id
        ? {
            logo_picture_url: action.payload.logo_picture_url,
            small_logo_picture_url: action.payload.small_logo_picture_url,
          }
        : {};
      setSettings((current) => ({ ...current!, ...logo }));
      syncShellCompanySpace({ ...values, ...logo });
      dispatch(addMessage({ severity: 'success', title: t(toastKey) }));
      return true;
    },
    [dispatch, loadSettings, t]
  );

  if (loadFailed && !settings) {
    return (
      <div className={'company-settings__body'}>
        <Alert
          severity={'error'}
          action={
            <Button color={'inherit'} size={'small'} onClick={loadSettings}>
              {t('companySettings.retry')}
            </Button>
          }
        >
          {t('companySettings.loadFailed')}
        </Alert>
      </div>
    );
  }

  if (!settings || !authUser) {
    return <LinearProgress />;
  }

  // AngularJS locks the page for the admin role only; a private space has no role at all
  const isOwner = space?.user_role !== 'admin';
  const tabProps: SettingsTabProps = { settings, isOwner, onSave };
  const content: Record<CompanySettingsTab, () => JSX.Element> = {
    organization: () => <OrganizationSettings {...tabProps} />,
    registration: () => <RegistrationSettings {...tabProps} />,
    workplaces: () => <WorkplaceSettings {...tabProps} />,
    content: () => <ContentSettings {...tabProps} />,
  };

  const tabs: PageTab[] = TABS.map((name) => ({
    tab: name,
    label: t(`companySettings.tabs.${name}`),
    content: () => (
      <div className={'company-settings__body'}>
        {!isOwner && name !== 'organization' && (
          <Alert severity={'info'}>{t('companySettings.ownerOnly')}</Alert>
        )}
        {content[name]()}
      </div>
    ),
  }));

  return (
    <div className={'company-settings layout-fill layout-column'}>
      <PageTabs
        tabs={tabs}
        currentTab={activeTab}
        onTabChange={(name) => {
          setActiveTab(name as CompanySettingsTab);
          setShellStateParam('tab', name);
        }}
      />
    </div>
  );
};
